import { resolvePaymentCardBrand } from '@frontend/api-client';
import { LoginHero, Visa } from '@frontend/assets';
import { dateFormatters, e164ToInternational, match } from '@frontend/duck-tape';
import type { ReactNode } from '@frontend/react';
import { useEffect, useState } from '@frontend/react';
import {
  AppShell,
  Button,
  ClerkLoading,
  ClerkUnavailable,
  Icon,
  Image,
  LoadingSpinnerPanel,
  ScrollableContentPanel,
  Text,
  Title,
  XStack,
  YStack,
  useSSOLoadState,
} from '@frontend/web-react';
import {
  CreditCardModal,
  StripeLoading,
  StripeProvider,
  StripeUnavailable,
  UnauthenticatedAppHeader,
} from '@frontend/web/components';
import {
  useAnalytics,
  useAuthentication,
  useListCardsQuery,
  useMcpFlow,
  useRetrieveClientQuery,
} from '@frontend/web/hooks';
import { createFileRoute } from '@frontend/web/utils';

const MCP_ANALYTICS_BASE = {
  entryType: 'mcp',
  flowVariant: 'mcp',
} as const;

const MCPSuccessLayout = ({ children }: { children: ReactNode }) => (
  <XStack className="h-screen">
    <ScrollableContentPanel>{children}</ScrollableContentPanel>
    <YStack className="bg-surfaceBrand hidden flex-1 justify-center md:flex">
      {/* eslint-disable-next-line tailwindcss/no-arbitrary-value */}
      <Image className="h-[500px] w-[960px]" source={LoginHero} />
    </YStack>
  </XStack>
);

const MCPSuccessContent = () => {
  const ssoLoadState = useSSOLoadState();
  const { clearMcpFlow } = useMcpFlow();
  const { logout } = useAuthentication();
  const { track } = useAnalytics();

  useEffect(() => {
    clearMcpFlow();
  }, [clearMcpFlow]);

  useEffect(() => {
    track({
      data: { ...MCP_ANALYTICS_BASE },
      event: 'mcpSuccessViewed',
      topic: 'auth',
    });
  }, [track]);

  const handleSignOut = async () => {
    track({
      data: { ...MCP_ANALYTICS_BASE },
      event: 'mcpSuccessSignedOut',
      topic: 'auth',
    });
    await logout();
    // eslint-disable-next-line fp/no-mutation
    window.location.href = '/signup/mcp';
  };

  const { data: client, isError: isClientError, isLoading: isClientLoading } = useRetrieveClientQuery();
  const { data: queryCards, isLoading: isCardsLoading } = useListCardsQuery();
  const [isCreditCardModalOpen, setIsCreditCardModalOpen] = useState(false);

  const cards = queryCards?.length ? queryCards : client?.cards;
  const defaultCard = cards?.find((card) => card.isDefault) ?? cards?.[0];

  if (ssoLoadState.status !== 'success') {
    return (
      <MCPSuccessLayout>
        {match(ssoLoadState)
          .with({ status: 'loading' }, () => <ClerkLoading />)
          .with({ status: 'failure' }, ({ onReload }) => <ClerkUnavailable onAction={onReload} />)
          .exhaustive()}
      </MCPSuccessLayout>
    );
  }

  if (isClientError || (!isClientLoading && !client)) {
    return (
      <MCPSuccessLayout>
        <YStack alignItems="center" className="flex-1" gap="md" justifyContent="center">
          <Title type="h5">Something went wrong</Title>
          <Text color="secondary">We couldn&apos;t load your account. Please try again.</Text>
          <Button
            onClick={() => {
              track({
                data: {
                  ...MCP_ANALYTICS_BASE,
                  errorCategory: 'unknown',
                  errorCode: 'CLIENT_LOAD_FAILED',
                  errorSource: 'mcpSuccess',
                  retriable: true,
                },
                event: 'mcpSuccessRetrySelected',
                topic: 'auth',
              });
              clearMcpFlow();
              // eslint-disable-next-line fp/no-mutation
              window.location.href = '/signup/mcp';
            }}
            variant="primary"
          >
            Try again
          </Button>
        </YStack>
      </MCPSuccessLayout>
    );
  }

  if (isClientLoading || isCardsLoading || !client) {
    return (
      <MCPSuccessLayout>
        <LoadingSpinnerPanel fullScreen />
      </MCPSuccessLayout>
    );
  }

  return (
    <>
      <MCPSuccessLayout>
        <YStack className="gap-y-lg">
          <YStack className="gap-y-sm">
            <Title type="h4">Welcome to Duckbill!</Title>
            <Text color="secondary">
              Your account has been successfully created. Here&apos;s a summary of your info.
            </Text>
          </YStack>

          <YStack className="gap-lg border-1 border-borderPrimary rounded-lg p-md bg-trueWhite">
            <YStack className="gap-md">
              <Text type="h4">
                {client.firstName} {client.lastName}
              </Text>
              <YStack className="gap-sm">
                <XStack alignItems="center" gapX="xs">
                  <Icon color="secondary" name="IconMail" size="sm" />
                  <Text color="secondary" type="caption">
                    {client.email}
                  </Text>
                </XStack>
                {client.phoneNumber ? (
                  <XStack alignItems="center" gapX="xs">
                    <Icon color="secondary" name="IconPhone" size="sm" />
                    <Text color="secondary" type="caption">
                      {e164ToInternational(client.phoneNumber) ?? 'Invalid phone number'}
                    </Text>
                  </XStack>
                ) : null}
                {client.birthday ? (
                  <XStack alignItems="center" gapX="xs">
                    <Icon color="secondary" name="IconCake" size="sm" />
                    <Text color="secondary" type="caption">
                      {dateFormatters['MM/DD/YYYY'](client.birthday)}
                    </Text>
                  </XStack>
                ) : null}
              </YStack>
            </YStack>
          </YStack>

          <YStack className="gap-md">
            <Text type="h5">Payment Method</Text>
            {defaultCard ? (
              <XStack
                accessibilityLabel="Default payment method"
                className="items-center justify-between border-1 border-borderPrimary rounded-lg p-md bg-trueWhite"
              >
                <XStack className="items-center gap-sm">
                  <Image
                    source={resolvePaymentCardBrand(defaultCard.brand, { fallback: Visa })}
                    style={{ height: 20, marginRight: 8, width: 32 }}
                  />
                  <Text type="h6">**** {defaultCard.lastFour}</Text>
                </XStack>
                <Text color="tertiary" type="p-sm">
                  Exp {defaultCard.month}/{defaultCard.year}
                </Text>
              </XStack>
            ) : (
              <XStack
                accessibilityLabel="Add payment method button"
                alignItems="center"
                className="bg-trueWhite p-md border-borderPrimary border-1 rounded-lg border-dashed"
                enableHoverEffect
                gapX="sm"
                onClick={() => {
                  track({
                    data: { ...MCP_ANALYTICS_BASE },
                    event: 'mcpAddPaymentMethodSelected',
                    topic: 'auth',
                  });
                  setIsCreditCardModalOpen(true);
                }}
              >
                <XStack alignItems="center" className="flex-1" gapX="sm">
                  <Icon name="IconCreditCard" size={24} />
                  <Text type="h6">Add Payment Method</Text>
                </XStack>
                <Icon name="IconPlus" />
              </XStack>
            )}
          </YStack>

          {!defaultCard ? (
            <Button
              fullWidth
              label="Add Payment Method"
              onClick={() => {
                track({
                  data: { ...MCP_ANALYTICS_BASE },
                  event: 'mcpAddPaymentMethodSelected',
                  topic: 'auth',
                });
                setIsCreditCardModalOpen(true);
              }}
              variant="secondary"
            />
          ) : null}

          <Button fullWidth onClick={handleSignOut} variant="tertiary">
            Sign Out
          </Button>
        </YStack>
      </MCPSuccessLayout>
      <CreditCardModal
        declinedCard={false}
        focusedCardId={undefined}
        isOpen={isCreditCardModalOpen}
        onClose={() => setIsCreditCardModalOpen(false)}
      />
    </>
  );
};

const MCPSuccess = () => (
  <AppShell>
    <UnauthenticatedAppHeader />
    <StripeProvider
      expectedElements={[]}
      loadingFallback={
        <MCPSuccessLayout>
          <StripeLoading />
        </MCPSuccessLayout>
      }
      reloadFallback={
        <MCPSuccessLayout>
          <StripeUnavailable onAction={() => window.location.reload()} variant="reload" />
        </MCPSuccessLayout>
      }
      retryFallback={(onRetry) => (
        <MCPSuccessLayout>
          <StripeUnavailable onAction={onRetry} variant="retry" />
        </MCPSuccessLayout>
      )}
    >
      <MCPSuccessContent />
    </StripeProvider>
  </AppShell>
);

export const Route = createFileRoute('/signup/mcp/success')({
  component: MCPSuccess,
});
